"use client"

import { useMemo, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import type { BackendPortfolio, PortfolioHistoryPoint, TransparencyRecord } from "@/lib/agent-force"
import { formatAgentName } from "@/lib/format-agent-name"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from "recharts"
import { Brain, Bot, Clock, Sparkles, FileJson, Search } from "lucide-react"
import { Spinner } from "@/components/ui/spinner"

const CHART_POINT_CAP = 240

function decimateChartPoints<T>(arr: T[], maxPoints: number): T[] {
  if (arr.length <= maxPoints) return arr
  const step = Math.ceil(arr.length / maxPoints)
  const out: T[] = []
  for (let i = 0; i < arr.length; i += step) {
    out.push(arr[i]!)
  }
  const last = arr[arr.length - 1]
  if (out[out.length - 1] !== last) {
    out.push(last!)
  }
  return out
}

function formatUsdCompact(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value)
}

function formatUsdFull(value: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(value)
}

function buildChartSeries(
  points: PortfolioHistoryPoint[],
  days: number,
): { date: string; value: number; recordedAt: string }[] {
  const now = Date.now()
  const windowMs = Math.max(1, days) * 24 * 60 * 60 * 1000
  const cutoff = now - windowMs
  const sorted = [...points]
    .filter((p) => {
      const t = new Date(p.recorded_at).getTime()
      return Number.isFinite(t) && t >= cutoff
    })
    .sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime())

  if (sorted.length === 0) {
    return []
  }

  const mapped = sorted.map((p) => {
    const d = new Date(p.recorded_at)
    return {
      date: d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        ...(days > 200 ? { year: "2-digit" as const } : {}),
      }),
      value: Math.round(Number(p.equity) || 0),
      recordedAt: p.recorded_at,
    }
  })
  return decimateChartPoints(mapped, CHART_POINT_CAP)
}

/** Append current MT5 equity so the line ends at the same figure as Total Portfolio Value. */
function mergeLiveEquityPoint(
  series: { date: string; value: number; recordedAt: string }[],
  liveEquity: number | null,
): { date: string; value: number; recordedAt: string }[] {
  if (liveEquity == null || !Number.isFinite(liveEquity)) {
    return series
  }
  const v = Math.round(liveEquity * 100) / 100
  if (series.length === 0) {
    return [
      {
        date: "Now",
        value: v,
        recordedAt: new Date().toISOString(),
      },
    ]
  }
  const last = series[series.length - 1]!
  if (Math.abs(last.value - v) < 1e-2) {
    return series
  }
  return [
    ...series,
    {
      date: "Now",
      value: v,
      recordedAt: new Date().toISOString(),
    },
  ]
}

const timeframes = [
  { label: "1D", days: 1 },
  { label: "1W", days: 7 },
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "YTD", days: 120 },
  { label: "1Y", days: 365 },
  { label: "All", days: 730 },
]

interface AgentThought {
  id: string
  agent: string
  timestamp: string
  /** ISO from the DB row (when this cycle was recorded in SQLite). */
  cycleRecordedAt?: string | null
  stage: string
  symbol?: string
  content: string
  status: "active" | "pending" | "completed"
  reasoning?: string[]
  rawResponse?: unknown
}

function prettyTime(ts: string) {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  const now = new Date()
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  }
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
}

function prettyRecordedAt(ts: string | null | undefined): string {
  if (!ts) return ""
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.round(diffMs / 60_000)
  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" })
}

function normalizeSymbol(symbol?: string) {
  return String(symbol || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
}

function stringifyPayload(payload: unknown): string {
  try {
    return JSON.stringify(payload ?? {}, null, 2)
  } catch {
    return String(payload)
  }
}

function findRecordForThought(records: TransparencyRecord[], thought: AgentThought | null): TransparencyRecord | null {
  if (!thought) return records[0] ?? null
  const sym = normalizeSymbol(thought.symbol)
  return (
    records.find((r) => normalizeSymbol(r.resolved_symbol || r.selected_symbol || r.requested_symbol) === sym) ??
    records.find((r) => normalizeSymbol(r.requested_symbol) === sym.slice(0, 6)) ??
    records[0] ??
    null
  )
}

function obj(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {}
}

/** Titles from market agent trace: full API list first, else curated headlines. */
function marketHeadlinesFromResponse(raw: unknown): string[] {
  const o = obj(raw)
  const fetched = o.fetched_headlines
  if (Array.isArray(fetched) && fetched.length > 0) {
    return fetched.map((h) => String(h)).filter(Boolean)
  }
  const headlines = o.headlines
  if (Array.isArray(headlines) && headlines.length > 0) {
    return headlines.map((h) => String(h)).filter(Boolean)
  }
  return []
}

function isMarketNewsTrace(thought: AgentThought | null): boolean {
  if (!thought) return false
  const agent = String(thought.agent || "").toLowerCase()
  const stage = String(thought.stage || "").toLowerCase()
  return agent.includes("market") && (stage.includes("news") || stage.includes("analyze"))
}

function isTechnicalAnalyzeTrace(thought: AgentThought | null): boolean {
  if (!thought) return false
  const agent = String(thought.agent || "").toLowerCase()
  const stage = String(thought.stage || "").toLowerCase()
  return agent.includes("technical") && stage.includes("analyze")
}

function technicalPriceDecimals(symbol: string): number {
  return symbol.toUpperCase().includes("JPY") ? 3 : 5
}

function formatTechScalar(n: unknown, decimals: number): string {
  const x = typeof n === "number" ? n : Number(n)
  if (!Number.isFinite(x)) return "—"
  return x.toFixed(decimals)
}

/** Pretty JSON matching backend `technical_agent` hypothesis (test-style payload). */
function TechnicalTraceDetail({ raw }: { raw: unknown }) {
  if (raw == null) {
    return (
      <div className="mt-3 border-t border-border/50 pt-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Technical hypothesis
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          No trade hypothesis — insufficient history, neutral context, or no qualifying buy/sell setup.
        </p>
      </div>
    )
  }

  const r = obj(raw)
  const sym = String(r.symbol ?? "")
  const dec = technicalPriceDecimals(sym)
  const direction = String(r.direction ?? "").toLowerCase()
  const actionable = direction === "buy" || direction === "sell"
  const conf = typeof r.confidence === "number" ? r.confidence : Number(r.confidence)
  const signalState = String(r.signal_state ?? "")

  const pretty = JSON.stringify(raw, null, 2)

  return (
    <div className="mt-3 border-t border-border/50 pt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {actionable ? "Technical analysis" : "Technical diagnostic (no entry)"}
        </p>
        {actionable ? (
          <Badge
            variant="outline"
            className={
              direction === "buy"
                ? "text-positive border-positive/40 bg-positive/10 text-xs"
                : "text-negative border-negative/40 bg-negative/10 text-xs"
            }
          >
            {direction.toUpperCase()}
          </Badge>
        ) : signalState ? (
          <Badge variant="outline" className="text-xs border-border text-muted-foreground">
            {signalState}
          </Badge>
        ) : null}
        {actionable && Number.isFinite(conf) ? (
          <span className="text-xs text-muted-foreground font-mono tabular-nums">confidence {conf.toFixed(2)}</span>
        ) : null}
        {sym ? <span className="text-xs font-mono text-primary">{sym}</span> : null}
      </div>

      <dl className="grid grid-cols-[minmax(0,7rem)_1fr] gap-x-3 gap-y-1.5 text-xs sm:text-sm">
        {actionable ? (
          <>
            <dt className="text-muted-foreground font-mono">entry</dt>
            <dd className="font-mono tabular-nums">{formatTechScalar(r.entry, dec)}</dd>
            <dt className="text-muted-foreground font-mono">stop_loss</dt>
            <dd className="font-mono tabular-nums">{formatTechScalar(r.stop_loss, dec)}</dd>
            <dt className="text-muted-foreground font-mono">take_profit</dt>
            <dd className="font-mono tabular-nums">{formatTechScalar(r.take_profit, dec)}</dd>
          </>
        ) : null}
        <dt className="text-muted-foreground font-mono">pattern_used</dt>
        <dd className="font-mono break-all">{String(r.pattern_used ?? "—")}</dd>
      </dl>

      {typeof r.reasoning === "string" && r.reasoning ? (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">reasoning</p>
          <p className="text-sm leading-snug text-foreground/90">{r.reasoning}</p>
        </div>
      ) : null}
      {typeof r.explanation === "string" && r.explanation ? (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">explanation</p>
          <p className="text-sm leading-relaxed text-foreground/90">{r.explanation}</p>
        </div>
      ) : null}

      <div>
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Full JSON (test-style)</p>
        <pre className="max-h-[min(45vh,360px)] overflow-y-auto overscroll-contain rounded-md border border-border/70 bg-background/80 p-3 text-[11px] leading-relaxed font-mono text-foreground/95 whitespace-pre-wrap break-words">
          {pretty}
        </pre>
      </div>
    </div>
  )
}

export function PortfolioChart({
  thoughts = [],
  transparencyRecords = [],
  portfolioHistory = [],
  portfolio = null,
  loading = false,
}: {
  thoughts?: AgentThought[]
  transparencyRecords?: TransparencyRecord[]
  portfolioHistory?: PortfolioHistoryPoint[]
  portfolio?: BackendPortfolio | null
  loading?: boolean
}) {
  const [selectedTimeframe, setSelectedTimeframe] = useState("1M")
  const [selectedThoughtId, setSelectedThoughtId] = useState<string | null>(thoughts[0]?.id ?? null)
  const days = timeframes.find((t) => t.label === selectedTimeframe)?.days || 30

  const liveEquity =
    portfolio != null && typeof portfolio.equity === "number" && Number.isFinite(portfolio.equity)
      ? portfolio.equity
      : null
  const liveBalance =
    portfolio != null && typeof portfolio.balance === "number" && Number.isFinite(portfolio.balance)
      ? portfolio.balance
      : null

  const baseData = useMemo(() => buildChartSeries(portfolioHistory ?? [], days), [portfolioHistory, days])
  const data = useMemo(() => mergeLiveEquityPoint(baseData, liveEquity), [baseData, liveEquity])

  const yDomain = useMemo((): [number, number] => {
    if (!data.length) return [0, 1]
    const vals = data.map((d) => d.value)
    const lo = Math.min(...vals)
    const hi = Math.max(...vals)
    const pad = Math.max(1, (hi - lo) * 0.06, Math.abs(hi) * 0.02)
    return [lo - pad, hi + pad]
  }, [data])

  const lastStoredValue =
    baseData.length > 0 ? baseData[baseData.length - 1]!.value : null
  const displayEquity =
    liveEquity ?? (data.length > 0 ? data[data.length - 1]!.value : 0)

  const startValue = data[0]?.value ?? 0
  const endValue = data[data.length - 1]?.value ?? 0
  const rangeChange = startValue > 0 ? ((endValue - startValue) / startValue) * 100 : endValue > 0 ? 100 : 0
  const rangePositive = rangeChange >= 0

  // Headline % change should follow selected chart range (1D/1W/1M/3M/YTD/1Y/All).
  const primaryPct = data.length >= 2 ? rangeChange : null
  const primaryPositive = primaryPct != null ? primaryPct >= 0 : rangePositive
  const primaryLabel =
    primaryPct != null ? `${primaryPositive ? "+" : ""}${primaryPct.toFixed(2)}%` : "—"
  const primaryTrendClass =
    primaryPct == null ? "text-muted-foreground" : primaryPositive ? "text-positive" : "text-negative"

  const latestMove = useMemo(() => {
    if (liveEquity != null && lastStoredValue != null && lastStoredValue > 0) {
      const delta = liveEquity - lastStoredValue
      const pct = (delta / lastStoredValue) * 100
      if (Math.abs(delta) < 0.02 && Math.abs(pct) < 0.01) {
        return null
      }
      return { pct, delta, up: liveEquity >= lastStoredValue }
    }
    if (data.length < 2) return null
    const prev = data[data.length - 2]!.value
    const last = data[data.length - 1]!.value
    if (!Number.isFinite(prev) || !Number.isFinite(last) || prev <= 0) return null
    const pct = ((last - prev) / prev) * 100
    const delta = last - prev
    if (Math.abs(delta) < 0.02 && Math.abs(pct) < 0.01) return null
    return { pct, delta, up: last >= prev }
  }, [data, liveEquity, lastStoredValue])

  const selectedThought = thoughts.find((t) => t.id === selectedThoughtId) ?? thoughts[0] ?? null
  const selectedRecord = findRecordForThought(transparencyRecords, selectedThought)
  const selectedMarket = obj(selectedRecord?.market)
  const selectedTechnical = obj(selectedRecord?.technical)
  const selectedPortfolio = obj(selectedRecord?.portfolio_agent)
  const selectedRun = obj(selectedRecord?.run_cycle_output)
  const selectedRunMarketContext = obj(selectedRun.market_context)
  const marketSignal = String(selectedMarket.market_signal ?? selectedRunMarketContext.market_signal ?? "n/a")
  const llmStack = [
    String(selectedTechnical.explanation ?? ""),
    String(selectedMarket.llm_analysis ?? ""),
    String(obj(selectedPortfolio.portfolio_analysis).llm_suggestion ? obj(obj(selectedPortfolio.portfolio_analysis).llm_suggestion).rationale ?? "" : ""),
  ].filter(Boolean)

  return (
    <Card className="col-span-full min-w-0 w-full lg:col-span-2 bg-card border-border">
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <CardTitle className="text-lg font-semibold">Portfolio Performance</CardTitle>
          <div className="mt-1 flex flex-wrap items-baseline gap-2 sm:gap-3">
            {loading ? (
              <>
                <Spinner className="size-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading portfolio history…</span>
              </>
            ) : data.length === 0 ? (
              <>
                <span className="text-3xl font-bold font-mono tabular-nums text-muted-foreground">—</span>
                <span className="text-sm font-medium text-muted-foreground">No history yet</span>
              </>
            ) : (
              <>
                <span className="text-3xl font-bold font-mono tabular-nums">{formatUsdFull(displayEquity)}</span>
                <span className={`text-sm font-medium ${primaryTrendClass}`}>
                  {primaryLabel}
                </span>
                {latestMove ? (
                  <span
                    className={`text-xs font-medium tabular-nums ${latestMove.up ? "text-positive" : "text-negative"}`}
                    title={
                      liveEquity != null && lastStoredValue != null
                        ? "Change from last stored snapshot to live MT5 equity"
                        : "Change between the last two points on the chart"
                    }
                  >
                    Last step: {latestMove.up ? "+" : ""}
                    {formatUsdFull(latestMove.delta)} ({latestMove.up ? "+" : ""}
                    {latestMove.pct.toFixed(2)}%)
                  </span>
                ) : null}
              </>
            )}
          </div>
          {!loading && data.length > 0 ? (
            <p className="text-[11px] text-muted-foreground">
              {liveEquity != null
                ? "Headline % is the selected timeframe change. The value is live MT5 equity, and the curve ends at the current MT5 tick."
                : "Headline % is the selected timeframe change based on stored snapshots."}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap gap-1">
          {timeframes.map((tf) => (
            <Button
              key={tf.label}
              variant={selectedTimeframe === tf.label ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => setSelectedTimeframe(tf.label)}
            >
              {tf.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[280px] min-h-[200px] w-full min-w-0">
          {loading ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-10 text-muted-foreground" />
              <span>Loading chart…</span>
            </div>
          ) : data.length === 0 ? (
            <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
              No portfolio history in SQLite yet. When MetaTrader is connected, the backend stores equity on a regular
              schedule and whenever total equity moves by a meaningful amount.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 5, right: 12, left: 5, bottom: 5 }}>
                <defs>
                  <linearGradient id="portfolioGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="oklch(0.70 0.18 250)" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="oklch(0.70 0.18 250)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.25 0.005 260)" vertical={false} />
                <XAxis dataKey="date" stroke="oklch(0.60 0 0)" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis
                  stroke="oklch(0.60 0 0)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                  tickFormatter={(value: number) => formatUsdCompact(value)}
                  domain={yDomain}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "oklch(0.14 0.005 260)",
                    border: "1px solid oklch(0.25 0.005 260)",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  formatter={(value: number) => [formatUsdFull(value), "Equity"]}
                  labelFormatter={(_label, items) => {
                    const row = items?.[0]?.payload as { recordedAt?: string } | undefined
                    const raw = row?.recordedAt
                    if (!raw) return ""
                    const d = new Date(raw)
                    return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleString()
                  }}
                  labelStyle={{ color: "oklch(0.60 0 0)" }}
                />
                {data.length > 0 && (
                  <ReferenceLine y={startValue} stroke="oklch(0.60 0 0)" strokeDasharray="5 5" strokeOpacity={0.5} />
                )}
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="oklch(0.70 0.18 250)"
                  strokeWidth={2}
                  fill="url(#portfolioGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Agent Intelligence Feed Section */}
        <div className="mt-6 pt-5 border-t border-border">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Brain className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">Agent Intelligence Feed</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{thoughts.length} events</Badge>
              <Badge variant="outline" className="text-xs">{transparencyRecords.length} symbols</Badge>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mb-4 leading-snug">
            Stored trace from SQLite — one latest cycle per symbol. Each event shows the time it was recorded.
            Different symbols may come from different cycle runs.
          </p>

          <div className="grid min-w-0 gap-4 lg:grid-cols-2">
            <ScrollArea className="h-[280px] min-w-0 pr-2">
              <div className="min-w-0 space-y-2">
                {thoughts.length === 0 && (
                  <div className="text-sm text-muted-foreground p-2">Waiting for agent events...</div>
                )}
                {thoughts.slice(0, 80).map((thought) => (
                  <button
                    key={thought.id}
                    onClick={() => setSelectedThoughtId(thought.id)}
                    className={`w-full min-w-0 rounded-md border p-2.5 text-left transition-colors ${
                      selectedThought?.id === thought.id ? "border-primary/50 bg-primary/10" : "border-border/60 bg-secondary/30 hover:bg-secondary/45"
                    }`}
                  >
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                        <Badge variant="outline" className="shrink-0 text-[10px]">
                          <Bot className="h-3 w-3 mr-1" />
                          {formatAgentName(thought.agent)}
                        </Badge>
                        <Badge variant="outline" className="shrink-0 text-[10px]">
                          {thought.stage}
                        </Badge>
                        {thought.symbol && (
                          <span className="text-[10px] font-mono text-primary">{thought.symbol}</span>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-0.5">
                        <span className="flex items-center text-[10px] text-muted-foreground">
                          <Clock className="h-3 w-3 mr-1" />
                          {prettyTime(thought.timestamp)}
                        </span>
                        {thought.cycleRecordedAt && (
                          <span className="text-[9px] text-muted-foreground/55 leading-none">
                            cycle {prettyRecordedAt(thought.cycleRecordedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-1.5 max-w-full overflow-x-auto overscroll-x-contain">
                      <p className="text-sm leading-snug line-clamp-2 whitespace-pre-wrap [overflow-wrap:anywhere]">
                        {thought.content}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>

            <div className="min-w-0 rounded-md border border-border/70 bg-secondary/20 p-3">
              {!selectedThought && (
                <div className="text-sm text-muted-foreground">Select an event to inspect full details.</div>
              )}
              {selectedThought && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline"><Bot className="h-3 w-3 mr-1" />{formatAgentName(selectedThought.agent)}</Badge>
                      <Badge variant="outline">{selectedThought.stage}</Badge>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-xs text-muted-foreground">{prettyTime(selectedThought.timestamp)}</span>
                      {selectedThought.cycleRecordedAt && (
                        <span className="text-[10px] text-muted-foreground/60">
                          Cycle recorded: {prettyRecordedAt(selectedThought.cycleRecordedAt)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="rounded-md border border-border/60 bg-background/40 p-2">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                      <Search className="h-3 w-3" /> Event Summary
                    </div>
                    <div className="max-h-[min(40vh,240px)] overflow-y-auto overscroll-contain pr-1">
                      <p className="text-sm">{selectedThought.content}</p>
                    </div>
                    {isTechnicalAnalyzeTrace(selectedThought) ? (
                      <TechnicalTraceDetail raw={selectedThought.rawResponse} />
                    ) : null}
                    {isMarketNewsTrace(selectedThought) && (() => {
                      const fromTrace = marketHeadlinesFromResponse(selectedThought.rawResponse)
                      const fromRecord = marketHeadlinesFromResponse(selectedMarket)
                      const lines = fromTrace.length > 0 ? fromTrace : fromRecord
                      if (lines.length === 0) {
                        return (
                          <p className="text-xs text-muted-foreground mt-2">
                            No headline titles in this response (empty article list or API limit).
                          </p>
                        )
                      }
                      return (
                        <div className="mt-3 pt-2 border-t border-border/50">
                          <p className="text-[11px] font-semibold text-muted-foreground mb-1.5">
                            Headlines returned ({lines.length})
                          </p>
                          <ul className="max-h-[200px] overflow-y-auto space-y-1.5 text-sm list-disc pl-4 marker:text-muted-foreground">
                            {lines.map((title, idx) => (
                              <li key={`${selectedThought.id}-hl-${idx}`} className="leading-snug">
                                {title}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    })()}
                  </div>

                  <div className="rounded-md border border-primary/30 bg-primary/5 p-2">
                    <div className="text-xs uppercase tracking-wide text-primary mb-1 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> LLM Insight Stack
                    </div>
                    {llmStack.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No LLM insight available for this symbol yet.</p>
                    ) : (
                      <div className="max-h-[min(35vh,200px)] overflow-y-auto overscroll-contain pr-1">
                        <ul className="space-y-1 text-sm">
                          {llmStack.map((line, idx) => (
                            <li key={`${selectedThought.id}-llm-${idx}`}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="rounded-md border border-border/60 bg-background/40 p-2">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                      <FileJson className="h-3 w-3" /> Raw Agent Response
                    </div>
                    <ScrollArea className="h-[140px]">
                      <pre className="text-[11px] leading-relaxed text-foreground/90 whitespace-pre-wrap break-all">
                        {stringifyPayload(selectedThought.rawResponse)}
                      </pre>
                    </ScrollArea>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    Symbol context: {String(selectedRecord?.requested_symbol ?? "n/a")} | Technical{" "}
                    {String(selectedTechnical.direction ?? "n/a")} @ confidence {String(selectedTechnical.confidence ?? "n/a")} | Market signal{" "}
                    {marketSignal}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
